import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  applyThemeClass,
  DARK_THEME,
  LIGHT_THEME,
  MODE_CLASS_KEY,
  persistThemeToggle,
  readPersistedTheme,
  resolveInitialTheme,
  type ThemeClass,
} from './themeClasses';
import { ThemeContext, type ThemeContextValue } from './ThemeContext';

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Resolve the visitor's theme up front (seeds `mode-class` from
  // prefers-color-scheme on a first visit; idempotent thereafter). Done in the
  // initializer rather than an effect so a child page that overrides the theme
  // on mount (the logo page) isn't clobbered by a late parent effect.
  const [theme, setThemeState] = useState<ThemeClass>(resolveInitialTheme);

  useEffect(() => {
    applyThemeClass(theme);
  }, [theme]);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== MODE_CLASS_KEY) return;
      setThemeState(readPersistedTheme() ?? LIGHT_THEME);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const toggle = useCallback(() => {
    setThemeState((previous) => {
      const next = previous === LIGHT_THEME ? DARK_THEME : LIGHT_THEME;
      persistThemeToggle(previous, next);
      return next;
    });
  }, []);

  const setTheme = useCallback((next: ThemeClass) => setThemeState(next), []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, isDark: theme === DARK_THEME, toggle, setTheme }),
    [theme, toggle, setTheme],
  );

  return <ThemeContext value={value}>{children}</ThemeContext>;
}
